import { Link } from 'react-router-dom';
import { Table } from '../../../design-system';
import {
  useReport, ReportCard, StatRow, Stat, BarList, GroupedBars,
  fmtMoney, fmtMoneyShort, fmtNum, fmtPct, pctTone, toneColor,
} from './reportsKit';

// The Billing tab reads the SAME ledger the Finance module writes: invoices
// raised, payments received, TDS withheld, what is still owed. It used to read
// Task.billItems and nothing else — so "Total billed" meant work PRICED, no
// invoice or receipt was ever reflected here, and the two screens described the
// same practice in numbers that could not be reconciled.
//
// The tiles are laid out along the money's actual journey, because that is the
// only way the gaps read as gaps:
//   accrued → unbilled → billed → collected → outstanding → overdue

const TREND_SERIES = [
  { key: 'billed', label: 'Billed (invoices raised)', color: toneColor('accent') },
  { key: 'collected', label: 'Collected (incl. TDS)', color: toneColor('green') },
];

const AGE_LABELS = {
  current: 'Not yet due',
  '0-30': 'Overdue 0–30 days',
  '31-60': 'Overdue 31–60 days',
  '61-90': 'Overdue 61–90 days',
  '90+': 'Overdue 90+ days',
};
const AGE_COLORS = {
  current: toneColor('slate'),
  '0-30': toneColor('amber'),
  '31-60': toneColor('amber'),
  '61-90': toneColor('red'),
  '90+': toneColor('red'),
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');
const ageDays = (d) => (d ? Math.max(0, Math.floor((Date.now() - new Date(d)) / 86400000)) : null);

export default function BillingReports({ range }) {
  const { data, loading, error } = useReport('billing-summary', range);
  const t = data?.totals || {};
  const clients = data?.byClient || [];
  const ageing = data?.ageing || {};

  // The single sentence that stops every "why doesn't this match?" question:
  // some figures are the period, some are the firm's standing position.
  const oldest = ageDays(t.oldestUnbilledAt);

  return (
    <div className="rep-stack">
      <ReportCard
        icon="💰"
        title="Revenue & Collections"
        subtitle="Straight from the invoice ledger — the same numbers as the Finance module"
        loading={loading}
        error={error}
        action={<Link className="wd-link" to="/org-admin/finance">Open Finance →</Link>}
      >
        <StatRow>
          <Stat
            label="Accrued"
            value={fmtMoney(t.accrued)}
            tone="slate"
            hint={`${fmtNum(t.chargeCount)} charges priced on work in this period`}
          />
          <Stat
            label="Not invoiced"
            value={fmtMoney(t.unbilled)}
            tone={t.unbilled > 0 ? 'amber' : 'green'}
            hint={oldest == null ? `${fmtNum(t.unbilledCount)} charges` : `${fmtNum(t.unbilledCount)} charges · oldest ${fmtNum(oldest)}d`}
          />
          <Stat
            label="Billed"
            value={fmtMoney(t.billed)}
            tone="accent"
            hint={`${fmtNum(t.invoiceCount)} invoices · ${fmtNum(t.clientsBilled)} clients`}
          />
          <Stat
            label="Collected"
            value={fmtMoney(t.settled)}
            tone="green"
            hint={`${fmtMoney(t.collected)} banked + ${fmtMoney(t.tdsCredited)} TDS`}
          />
          <Stat
            label="Outstanding"
            value={fmtMoney(t.outstanding)}
            tone={t.outstanding > 0 ? 'amber' : 'green'}
            hint={`${fmtNum(t.openCount)} open invoices · all time`}
          />
          <Stat
            label="Overdue"
            value={fmtMoney(t.overdue)}
            tone={t.overdue > 0 ? 'red' : 'green'}
            hint={`${fmtNum(t.overdueCount)} past their due date`}
          />
          <Stat
            label="Realisation"
            value={fmtPct(t.realisationPct)}
            tone={pctTone(t.realisationPct)}
            hint="Collected ÷ billed this period"
          />
        </StatRow>

        <p className="rep-note">Billed vs collected, per month</p>
        <GroupedBars
          data={data?.byMonth || []}
          series={TREND_SERIES}
          valueFmt={fmtMoney}
          emptyText="Nothing invoiced or collected in this period"
        />

        <p className="rep-note-plain">
          Accrued, billed and collected are measured over the selected dates. Not-invoiced,
          outstanding, overdue and the ageing below are the firm's standing position across all
          time — money owed since last year is still owed today, and a windowed figure would hide
          exactly the oldest debts.
          {t.proformaValue > 0 && (
            <> {fmtMoney(t.proformaValue)} sits with clients on {fmtNum(t.proformaCount)} proforma
            {t.proformaCount === 1 ? '' : 's'} — neither billed nor owed until the tax invoice is raised.</>
          )}
          {t.draftValue > 0 && <> {fmtMoney(t.draftValue)} is still in draft.</>}
        </p>
      </ReportCard>

      <div className="rep-grid">
        <ReportCard
          icon="⏳"
          title="Receivables Ageing"
          subtitle="How long the money owed has been waiting"
          loading={loading}
          error={error}
        >
          <BarList
            items={Object.entries(ageing).map(([k, v]) => ({
              label: `${AGE_LABELS[k] || k} · ${fmtNum(v.count)}`,
              value: v.amount,
              color: AGE_COLORS[k],
            }))}
            valueFmt={fmtMoney}
            emptyText="Nothing outstanding"
          />
        </ReportCard>

        <ReportCard
          icon="🧾"
          title="What was billed for"
          subtitle="Professional fees, reimbursed expenses and the GST on top"
          loading={loading}
          error={error}
        >
          <BarList
            items={[
              { label: 'Professional fees', value: t.billedProfessional || 0, color: toneColor('accent') },
              { label: 'OPE (reimbursed)', value: t.billedOpe || 0, color: toneColor('blue') },
              { label: 'GST', value: t.billedTax || 0, color: toneColor('slate') },
            ]}
            valueFmt={fmtMoney}
            emptyText="Nothing billed in this period"
          />
          <p className="rep-note-plain">
            Fees and OPE are the pre-tax value ({fmtMoney(t.billedNet)}); with GST that is
            {' '}{fmtMoney(t.billed)} billed. Work priced but never invoiced —
            {' '}{fmtMoney(t.unbilled)} — is not in any of these bars.
          </p>
        </ReportCard>
      </div>

      <ReportCard
        icon="📈"
        title="Revenue by Client (Concentration)"
        subtitle="Top clients by invoiced value and their share of the book"
        loading={loading}
        error={error}
      >
        <BarList
          items={clients.filter((c) => c.billed > 0).slice(0, 10)
            .map((c) => ({ label: `${c.name} · ${c.sharePct}%`, value: c.billed }))}
          valueFmt={fmtMoneyShort}
          color={toneColor('accent')}
          emptyText="No invoices raised in this period"
        />
      </ReportCard>

      <ReportCard
        icon="🏢"
        title="Client Ledger"
        subtitle="Priced, billed, collected and still owed — per client"
        loading={loading}
        error={error}
      >
        <Table
          keyField="id"
          columns={[
            {
              key: 'name',
              header: 'Client',
              render: (r) => (
                <>
                  <Link className="wd-link" to={`/org-admin/finance?tab=clients&client=${r.id}`}>{r.name}</Link>
                  {r.code && <span className="ftrk-code"> {r.code}</span>}
                </>
              ),
            },
            { key: 'accrued', header: 'Accrued', render: (r) => fmtMoney(r.accrued) },
            {
              key: 'unbilled',
              header: 'Not invoiced',
              render: (r) => (r.unbilled > 0
                ? <span className="rep-tone-amber"><strong>{fmtMoney(r.unbilled)}</strong></span>
                : '—'),
            },
            { key: 'billed', header: 'Billed', render: (r) => <strong>{fmtMoney(r.billed)}</strong> },
            {
              key: 'collected',
              header: 'Collected',
              render: (r) => (
                <>
                  {fmtMoney(r.collected + r.tdsCredited)}
                  {r.tdsCredited > 0 && <span className="ftrk-code"> incl. {fmtMoney(r.tdsCredited)} TDS</span>}
                </>
              ),
            },
            { key: 'outstanding', header: 'Outstanding', render: (r) => fmtMoney(r.outstanding) },
            {
              key: 'overdue',
              header: 'Overdue',
              render: (r) => (r.overdue > 0
                ? <span className="rep-tone-red"><strong>{fmtMoney(r.overdue)}</strong></span>
                : '—'),
            },
            {
              key: 'lastInvoiceAt',
              header: 'Last invoice',
              render: (r) => fmtDate(r.lastInvoiceAt),
            },
          ]}
          data={clients}
        />
        {clients.length === 0 && !loading && (
          <p className="rep-empty">No client has a billing position in this period.</p>
        )}
      </ReportCard>

      <ReportCard
        icon="🚰"
        title="Revenue Leak"
        subtitle="Work the firm has priced and never invoiced — by client, oldest first"
        loading={loading}
        error={error}
      >
        <StatRow>
          <Stat label="Never invoiced" value={fmtMoney(t.unbilled)} tone={t.unbilled > 0 ? 'red' : 'green'} hint={`${fmtNum(t.unbilledCount)} charges`} />
          <Stat label="Ever priced" value={fmtMoney(t.chargesTotal)} tone="slate" hint={`${fmtNum(t.chargesTotalCount)} charges, all time`} />
          <Stat label="Reached a document" value={fmtPct(t.billedCoveragePct)} tone={pctTone(t.billedCoveragePct)} hint="Of everything ever priced" />
          <Stat label="Oldest waiting" value={oldest == null ? '—' : `${fmtNum(oldest)}d`} tone={oldest > 60 ? 'red' : oldest > 30 ? 'amber' : 'slate'} hint={fmtDate(t.oldestUnbilledAt)} />
        </StatRow>
        <BarList
          items={clients.filter((c) => c.unbilled > 0)
            .sort((a, b) => new Date(a.oldestUnbilledAt || 0) - new Date(b.oldestUnbilledAt || 0))
            .slice(0, 12)
            .map((c) => ({
              label: `${c.name} · ${fmtNum(ageDays(c.oldestUnbilledAt))}d waiting`,
              value: c.unbilled,
              color: toneColor(ageDays(c.oldestUnbilledAt) > 60 ? 'red' : 'amber'),
            }))}
          valueFmt={fmtMoney}
          emptyText="Every charge has reached an invoice."
        />
      </ReportCard>

      <ReportCard
        icon="🧮"
        title="Is priced work turning into invoices?"
        subtitle="What the work was charged at, against what was actually raised — month by month"
        loading={loading}
        error={error}
      >
        <GroupedBars
          data={data?.byMonth || []}
          series={[
            { key: 'accrued', label: 'Priced on tasks', color: toneColor('slate') },
            { key: 'billed', label: 'Invoiced', color: toneColor('accent') },
          ]}
          valueFmt={fmtMoney}
          emptyText="No work priced or billed in this period"
        />
        <p className="rep-note-plain">
          The two bars never match exactly: a charge is raised when the work is done and invoiced
          later, and only the invoice carries GST. A month where the grey bar towers over the
          purple one is work that has not been billed yet.
        </p>
      </ReportCard>
    </div>
  );
}
